/* =============================================================
   BentoKids — Premium Hero Slider Engine
   Transitions: slide | fade | zoom | kenburns | parallax
   Features: autoplay, touch/swipe, keyboard, mouse drag,
             pause on hover, infinite loop, progress bar,
             dots, arrows, ARIA
   ============================================================= */
(function () {
  'use strict';

  /* -----------------------------------------------------------
     COUNTDOWN TIMER
  ----------------------------------------------------------- */
  function BKCountdown(el) {
    var target = new Date(el.dataset.date).getTime();
    if (isNaN(target)) return;
    var parts = el.querySelectorAll('[data-bkcd]');
    function tick() {
      var diff = target - Date.now();
      if (diff < 0) diff = 0;
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      parts.forEach(function (p) {
        var k = p.dataset.bkcd;
        var v = k === 'days' ? d : k === 'hours' ? h : k === 'mins' ? m : s;
        p.textContent = String(v).padStart(2, '0');
      });
      if (diff > 0) setTimeout(tick, 1000);
    }
    tick();
  }

  /* -----------------------------------------------------------
     MAIN SLIDER CLASS
  ----------------------------------------------------------- */
  function BKHeroSlider(el) {
    this.el = el;
    this.track = el.querySelector('.bkh-track');
    this.slides = Array.from(el.querySelectorAll('.bkh-slide'));
    this.total = this.slides.length;
    this.current = 0;
    this.animating = false;
    this.timer = null;
    this.progTimer = null;
    this.progStart = null;

    /* Settings from data attributes */
    this.autoplay = el.dataset.autoplay === 'true';
    this.speed = parseInt(el.dataset.speed, 10) || 5000;
    this.duration = parseInt(el.dataset.duration, 10) || 700;
    this.transition = el.dataset.transition || 'slide';
    this.infinite = el.dataset.infinite !== 'false';
    this.pauseHover = el.dataset.pauseHover !== 'false';

    /* Controls */
    this.prevBtn = el.querySelector('.bkh-arrow--prev');
    this.nextBtn = el.querySelector('.bkh-arrow--next');
    this.dotsWrap = el.querySelector('.bkh-dots');
    this.dotBtns = this.dotsWrap ? Array.from(this.dotsWrap.querySelectorAll('.bkh-dot')) : [];
    this.progBar = el.querySelector('.bkh-progress__bar');
    this.numCur = el.querySelector('.bkh-num__cur');
    this.numTot = el.querySelector('.bkh-num__tot');

    if (this.total < 2) { this._initFirst(); return; }

    this._initSlides();
    this._bindControls();
    this._bindTouch();
    this._bindKeyboard();
    this._bindMouseDrag();
    if (this.pauseHover) this._bindPause();
    if (this.numTot) this.numTot.textContent = String(this.total).padStart(2, '0');

    // Default first slide active on page load
    this.slides[0].classList.add('is-active');
    this.slides[0].setAttribute('aria-hidden', 'false');
    this.slides[0].setAttribute('tabindex', '0');

    this._updateDots();
    this._updateNum();

    if (this.autoplay) this._startAutoplay();

    /* Countdowns */
    el.querySelectorAll('[data-bkcd-root]').forEach(function (cd) { new BKCountdown(cd); });
  }

  BKHeroSlider.prototype._initFirst = function () {
    if (this.slides[0]) this.slides[0].classList.add('is-active');
    this.el.querySelectorAll('[data-bkcd-root]').forEach(function (cd) { new BKCountdown(cd); });
  };

  BKHeroSlider.prototype._initSlides = function () {
    var self = this;
    BKHeroSlider.prototype._initSlides = function () {
      var self = this;

      this.slides.forEach(function (slide, i) {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');

        var lazy = slide.dataset.lazySrc;
        if (lazy) { slide.style.backgroundImage = 'url(' + lazy + ')'; }
      });
    };
  };

  /* ── ACTIVATE ── */
  BKHeroSlider.prototype._activate = function (idx, dir) {
    var self = this;
    if (this.animating) return;
    if (idx === this.current && this.slides[idx].classList.contains('is-active')) return;

    this.animating = true;
    var prev = this.current;
    this.current = ((idx % this.total) + this.total) % this.total;

    var prevSlide = this.slides[prev];
    var nextSlide = this.slides[this.current];

    /* Transition dispatch */
    var fn = {
      fade: '_transitionFade',
      zoom: '_transitionZoom',
      kenburns: '_transitionKenBurns',
      parallax: '_transitionParallax',
      slide: '_transitionSlide'
    }[this.transition] || '_transitionSlide';

    /* Per-slide override */
    var slideTransition = nextSlide.dataset.transition || this.transition;
    fn = {
      fade: '_transitionFade',
      zoom: '_transitionZoom',
      kenburns: '_transitionKenBurns',
      parallax: '_transitionParallax',
      slide: '_transitionSlide'
    }[slideTransition] || fn;

    if (dir === 'none') {
      nextSlide.classList.add('is-active');
      nextSlide.setAttribute('aria-hidden', 'false');
      nextSlide.setAttribute('tabindex', '0');
      this._animDone(prevSlide, nextSlide);
    } else {
      this[fn](prevSlide, nextSlide, dir);
    }

    /* Update UI */
    this._updateDots();
    this._updateNum();
    this._updateProgress();
  };

  BKHeroSlider.prototype._animDone = function (prevSlide, nextSlide) {

    var self = this;

    /* Reset previous slide */
    prevSlide.classList.remove('is-active', 'is-leaving');
    prevSlide.setAttribute('aria-hidden', 'true');
    prevSlide.setAttribute('tabindex', '-1');

    prevSlide.style.cssText = '';

    /* Reset previous parallax background */
    var prevBg = prevSlide.querySelector('.bkh-bg');
    if (prevBg) {
      prevBg.style.transition = '';
      prevBg.style.transform = '';
    }


    /* Reset next slide */
    nextSlide.classList.remove('is-entering');

    var nextBg = nextSlide.querySelector('.bkh-bg');
    if (nextBg) {
      nextBg.style.transition = '';
      nextBg.style.transform = '';
    }


    self.animating = false;
  };

  /* ── SLIDE TRANSITION ── */
  BKHeroSlider.prototype._transitionSlide = function (prev, next, dir) {
    var self = this;
    var offset = dir === 'prev' ? '-100%' : '100%';
    var endOffset = dir === 'prev' ? '100%' : '-100%';

    next.style.transform = 'translateX(' + offset + ')';
    next.style.transition = 'none';
    next.classList.add('is-active', 'is-entering');
    next.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var d = self.duration + 'ms';
        prev.style.transition = 'transform ' + d + ' cubic-bezier(.4,0,.2,1)';
        prev.style.transform = 'translateX(' + endOffset + ')';
        next.style.transition = 'transform ' + d + ' cubic-bezier(.4,0,.2,1)';
        next.style.transform = 'translateX(0)';
        prev.classList.add('is-leaving');
        setTimeout(function () { self._animDone(prev, next); }, self.duration + 50);
      });
    });
  };

  /* ── FADE TRANSITION ── */
  BKHeroSlider.prototype._transitionFade = function (prev, next) {
    var self = this;
    next.style.opacity = '0';
    next.style.transition = 'none';
    next.classList.add('is-active', 'is-entering');
    next.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        next.style.transition = 'opacity ' + self.duration + 'ms ease';
        next.style.opacity = '1';
        prev.style.transition = 'opacity ' + self.duration + 'ms ease';
        prev.style.opacity = '0';
        prev.classList.add('is-leaving');
        setTimeout(function () { self._animDone(prev, next); }, self.duration + 50);
      });
    });
  };

  /* ── ZOOM TRANSITION ── */
  BKHeroSlider.prototype._transitionZoom = function (prev, next) {
    var self = this;
    next.style.opacity = '0';
    next.style.transform = 'scale(1.08)';
    next.style.transition = 'none';
    next.classList.add('is-active', 'is-entering');
    next.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var d = self.duration + 'ms';
        next.style.transition = 'opacity ' + d + ' ease, transform ' + d + ' ease';
        next.style.opacity = '1';
        next.style.transform = 'scale(1)';
        prev.style.transition = 'opacity ' + d + ' ease';
        prev.style.opacity = '0';
        prev.classList.add('is-leaving');
        setTimeout(function () { self._animDone(prev, next); }, self.duration + 50);
      });
    });
  };

  /* ── KEN BURNS TRANSITION ── */
  BKHeroSlider.prototype._transitionKenBurns = function (prev, next) {
    var self = this;
    next.style.opacity = '0';
    next.style.transition = 'none';
    next.classList.add('is-active', 'is-entering', 'bkh-kb');
    next.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        next.style.transition = 'opacity ' + self.duration + 'ms ease';
        next.style.opacity = '1';
        prev.style.transition = 'opacity ' + self.duration + 'ms ease';
        prev.style.opacity = '0';
        prev.classList.add('is-leaving');
        setTimeout(function () {
          next.classList.remove('bkh-kb');
          self._animDone(prev, next);
        }, self.duration + 50);
      });
    });
  };

  /* ── PARALLAX TRANSITION ── */
  BKHeroSlider.prototype._transitionParallax = function (prev, next, dir) {
    var self = this;
    var offset = dir === 'prev' ? '-30%' : '30%';
    var endOffset = dir === 'prev' ? '30%' : '-30%';
    var prevBg = prev.querySelector('.bkh-bg');
    var nextBg = next.querySelector('.bkh-bg');

    next.style.opacity = '0';
    next.style.transition = 'none';
    if (nextBg) { nextBg.style.transform = 'translateX(' + offset + ')'; nextBg.style.transition = 'none'; }
    next.classList.add('is-active', 'is-entering');
    next.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        var d = self.duration + 'ms';
        next.style.transition = 'opacity ' + d + ' ease';
        next.style.opacity = '1';
        if (nextBg) {
          nextBg.style.transition = 'transform ' + d + ' cubic-bezier(.4,0,.2,1)';
          nextBg.style.transform = 'translateX(0)';
        }
        if (prevBg) {
          prevBg.style.transition = 'transform ' + d + ' cubic-bezier(.4,0,.2,1)';
          prevBg.style.transform = 'translateX(' + endOffset + ')';
        }
        prev.style.transition = 'opacity ' + d + ' ease';
        prev.style.opacity = '0';
        prev.classList.add('is-leaving');
        setTimeout(function () { self._animDone(prev, next); }, self.duration + 50);
      });
    });
  };

  /* ── NAVIGATION ── */
  BKHeroSlider.prototype.goTo = function (idx) {
    var dir = idx > this.current ? 'next' : 'prev';
    this._activate(idx, dir);
    if (this.autoplay) { clearTimeout(this.timer); this._startAutoplay(); }
  };

  BKHeroSlider.prototype.next = function () {
    var idx = this.infinite
      ? (this.current + 1) % this.total
      : Math.min(this.current + 1, this.total - 1);
    this._activate(idx, 'next');
    if (this.autoplay) { clearTimeout(this.timer); this._startAutoplay(); }
  };

  BKHeroSlider.prototype.prev = function () {
    var idx = this.infinite
      ? (this.current - 1 + this.total) % this.total
      : Math.max(this.current - 1, 0);
    this._activate(idx, 'prev');
    if (this.autoplay) { clearTimeout(this.timer); this._startAutoplay(); }
  };

  /* ── AUTOPLAY ── */
  BKHeroSlider.prototype._startAutoplay = function () {
    var self = this;
    clearTimeout(this.timer);
    this.timer = setTimeout(function () {
      self.next();
      self._startAutoplay();
    }, this.speed);
    this._startProgress();
  };

  BKHeroSlider.prototype._startProgress = function () {
    var self = this;
    if (!this.progBar) return;
    clearTimeout(this.progTimer);
    this.progBar.style.transition = 'none';
    this.progBar.style.width = '0%';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        self.progBar.style.transition = 'width ' + self.speed + 'ms linear';
        self.progBar.style.width = '100%';
      });
    });
  };

  BKHeroSlider.prototype._updateProgress = function () {
    if (this.autoplay) return; /* autoplay handles its own progress */
    if (!this.progBar) return;
    this.progBar.style.transition = 'none';
    this.progBar.style.width = ((this.current + 1) / this.total * 100) + '%';
  };

  /* ── DOTS & NUM ── */
  BKHeroSlider.prototype._updateDots = function () {
    var cur = this.current;
    this.dotBtns.forEach(function (d, i) {
      d.classList.toggle('is-active', i === cur);
      d.setAttribute('aria-selected', i === cur ? 'true' : 'false');
    });
  };

  BKHeroSlider.prototype._updateNum = function () {
    if (this.numCur) this.numCur.textContent = String(this.current + 1).padStart(2, '0');
  };

  /* ── BIND CONTROLS ── */
  BKHeroSlider.prototype._bindControls = function () {
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.prev(); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.next(); });
    this.dotBtns.forEach(function (d, i) {
      d.addEventListener('click', function () { self.goTo(i); });
    });
  };

  /* ── TOUCH / SWIPE ── */
  BKHeroSlider.prototype._bindTouch = function () {
    var self = this;
    var startX = 0;
    this.el.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
    }, { passive: true });
    this.el.addEventListener('touchend', function (e) {
      var diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? self.next() : self.prev(); }
    }, { passive: true });
  };

  /* ── MOUSE DRAG ── */
  BKHeroSlider.prototype._bindMouseDrag = function () {
    var self = this;
    var startX = 0;
    var dragging = false;
    this.el.addEventListener('mousedown', function (e) {
      startX = e.clientX; dragging = true;
    });
    this.el.addEventListener('mouseup', function (e) {
      if (!dragging) return;
      var diff = startX - e.clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? self.next() : self.prev(); }
      dragging = false;
    });
    this.el.addEventListener('mouseleave', function () { dragging = false; });
  };

  /* ── KEYBOARD ── */
  BKHeroSlider.prototype._bindKeyboard = function () {
    var self = this;
    this.el.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { self.prev(); }
      if (e.key === 'ArrowRight') { self.next(); }
    });
  };

  /* ── PAUSE ON HOVER ── */
  BKHeroSlider.prototype._bindPause = function () {
    var self = this;
    this.el.addEventListener('mouseenter', function () {
      clearTimeout(self.timer);
      if (self.progBar) { self.progBar.style.transition = 'none'; }
    });
    this.el.addEventListener('mouseleave', function () {
      if (self.autoplay) self._startAutoplay();
    });
  };

  /* -----------------------------------------------------------
     INIT ALL SLIDERS
  ----------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-bk-hero]').forEach(function (el) {
      new BKHeroSlider(el);
    });
  });

  window.BKHeroSlider = BKHeroSlider;

}());